import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { deleteAsset as deleteAssetApi, getAssets } from "../../../../Api/AssetApi";
import CODE from "../../../../Static/Constants/StatusCodes";

const DEFAULT_LIMIT = 25;

const INITIAL_QUERY = {
    // Bumped to force a refetch when nothing else about the query changed --
    // deleting the last row of page 1, or editing a row in place. Never sent
    // to the server.
    refresh: 0,
    page: 1,
    limit: DEFAULT_LIMIT,
    searchTerm: "",
    searchBy: null,
    sortBy: null,
    sortOrder: null,
    // The filter panel's selection, already mapped to request params
    // (assetTypeCode, stateCode, ...). IN the query object like
    // every other narrowing input,
    // not beside it, so a filter change and its page reset are ONE state
    // update rather than two renders and two requests.
    //
    // One bag rather than a field each: the panel is config-driven, so which
    // keys exist is a pageMeta decision, and a named field per filter would
    // have to be edited every time an org declares another one.
    filterParams: {},
};

// List state for the Assets tab: fetch, paginate, search, sort and apply the
// config-driven filter panel.
//
// EVERY narrowing input lives in ONE state object, and each change writes the
// whole new shape in a single update. Split across two pieces of state, a
// change to one is committed in a different render than the reset of the
// other, and each commit fires a request: the first for the new filter
// carrying the previous page, the second with the page reset. That shape was a
// real shipped bug on the Network tab (fixed in 820779fc) and this hook keeps
// its corrected shape deliberately -- see useNetworkEntities.js.
/**
 * @param {Object} [options]
 * @param {Object} [options.pinnedFilters] Narrowing the PAGE imposes, which the
 *   user cannot clear -- the entity page's `currentEntityID`, for instance.
 *   Kept out of `query.filterParams` on purpose: that object is what the filter
 *   panel owns and resets, so a pin living there would vanish the first time
 *   somebody pressed Clear and the page would silently widen to every asset in
 *   the org. Merged into requestArgs instead, which the export reads too, so a
 *   downloaded file cannot cover rows the page never showed.
 */
export const useAssets = ({ pinnedFilters = null } = {}) => {
    const [records, setRecords] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [query, setQuery] = useState(INITIAL_QUERY);

    // Guards against a slow earlier response overwriting a faster later one --
    // easy to hit by typing, where the first keystroke can land after the
    // third.
    const requestSeq = useRef(0);

    // The search/sort/filter half of the request, shared VERBATIM with the
    // export so a downloaded file always covers exactly the rows on screen.
    // A new object literal every render would re-run every effect below, so
    // the pin is compared by VALUE -- the same rule the filter params follow.
    const pinnedKey = JSON.stringify(pinnedFilters || {});

    const requestArgs = useMemo(
        () => ({
            search: query.searchTerm || undefined,
            searchBy: query.searchBy || undefined,
            sortBy: query.sortBy || undefined,
            sortOrder: query.sortOrder || undefined,
            ...query.filterParams,
            // LAST, so a pin always wins. A user filter on the same field
            // would otherwise widen the page past what it is for.
            ...JSON.parse(pinnedKey),
        }),
        [query, pinnedKey]
    );

    const fetchList = useCallback(async () => {
        const seq = ++requestSeq.current;
        setIsLoading(true);
        setErrorMessage("");

        const res = await getAssets({
            ...requestArgs,
            page: query.page,
            limit: query.limit,
        });

        // Superseded by a later request: leave every piece of state, the
        // loading flag included, to the winner. The winner always holds the
        // highest seq, so it always reaches the setIsLoading(false) below --
        // clearing the flag here would let a slow loser turn the spinner off
        // while the request the user is waiting on is still in flight.
        if (seq !== requestSeq.current) return;

        if (res?.responseStatus?.code === CODE.SUCCESS) {
            setRecords(res?.data?.records || []);
            setTotal(res?.data?.pagination?.total || 0);
        } else if (res?.responseStatus?.code === CODE.SESSION_EXPIRED) {
            setRecords([]);
            setTotal(0);
            setErrorMessage("Your session has expired. Please sign in again.");
        } else {
            setRecords([]);
            setTotal(0);
            setErrorMessage(res?.responseStatus?.msg || "Could not load the assets");
        }
        setIsLoading(false);
        // `query` whole, not its parts: that is what carries `refresh`, so a
        // write can force a refetch without pretending some filter moved.
    }, [query, requestArgs]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const setPage = useCallback((page) => {
        setQuery((prev) => (prev.page === page ? prev : { ...prev, page }));
    }, []);

    const setLimit = useCallback((limit) => {
        setQuery((prev) =>
            prev.limit === limit ? prev : { ...prev, limit, page: 1 }
        );
    }, []);

    // page:1 in the SAME update as the search -- not a follow-up effect. Bails
    // when nothing changed, so re-submitting the same term is not a second
    // request.
    const applySearch = useCallback((term, by) => {
        setQuery((prev) => {
            const searchTerm = term || "";
            const searchBy = by || null;
            if (prev.searchTerm === searchTerm && prev.searchBy === searchBy) {
                return prev;
            }
            return { ...prev, searchTerm, searchBy, page: 1 };
        });
    }, []);

    const applySort = useCallback((by, order) => {
        setQuery((prev) => {
            const sortBy = by || null;
            const sortOrder = order || null;
            if (prev.sortBy === sortBy && prev.sortOrder === sortOrder) {
                return prev;
            }
            return { ...prev, sortBy, sortOrder, page: 1 };
        });
    }, []);

    // The filter panel comes through here.
    //
    // Same single-update rule as applySearch: the caller hands back its whole
    // selection already mapped, so a shallow compare of the mapped params is
    // what decides whether anything actually changed. Bailing matters because
    // the panel calls Apply whether or not the user moved a tick.
    const applyFilterParams = useCallback((filterParams) => {
        setQuery((prev) => {
            const next = filterParams || {};
            const prevKeys = Object.keys(prev.filterParams);
            const nextKeys = Object.keys(next);
            const unchanged =
                prevKeys.length === nextKeys.length &&
                nextKeys.every(
                    (key) => String(prev.filterParams[key]) === String(next[key])
                );
            return unchanged ? prev : { ...prev, filterParams: next, page: 1 };
        });
    }, []);

    // The state change IS the list refetch. Calling fetchList() as well would
    // send a request built from the pre-update query -- the page that just
    // emptied -- followed by the corrected one. `refresh` exists so that a
    // write which does not move the page number still changes the query and so
    // still refetches.
    const refetch = useCallback((pageAdjust = (page) => page) => {
        setQuery((prev) => ({
            ...prev,
            page: pageAdjust(prev.page),
            refresh: (prev.refresh || 0) + 1,
        }));
    }, []);

    const removeAsset = useCallback(
        async (assetID, deleteReason) => {
            const res = await deleteAssetApi(assetID, deleteReason);
            const ok = res?.responseStatus?.code === CODE.SUCCESS;
            if (ok) {
                // Step back a page when the row was the only one left on it:
                // totals and pagination move with the deletion.
                refetch((page) =>
                    records.length === 1 && page > 1 ? page - 1 : page
                );
            }
            return { ok, message: res?.responseStatus?.msg, response: res };
        },
        [records.length, refetch]
    );

    return {
        records,
        total,
        isLoading,
        errorMessage,
        page: query.page,
        limit: query.limit,
        setPage,
        setLimit,
        applySearch,
        applySort,
        applyFilterParams,
        requestArgs,
        refetch,
        removeAsset,
    };
};

export default useAssets;
